import { useMemo } from 'react';
import { buildAnnualHeatmap } from '@/lib/analytics-calculations';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface AnnualHeatmapProps { dates: string[]; values: number[]; label?: string; }

const COLORS = ['bg-muted/30', 'bg-emerald-900/60', 'bg-emerald-700/60', 'bg-emerald-500/60', 'bg-emerald-400/80', 'bg-emerald-300'];
const DAY_LABELS = ['M', '', 'W', '', 'F', '', ''];

function level(value: number, max: number): number {
  if (max <= 0 || value <= 0) return 0;
  const r = value / max;
  if (r <= 0.1) return 1; if (r <= 0.3) return 2; if (r <= 0.5) return 3; if (r <= 0.7) return 4; return 5;
}

export function AnnualHeatmap({ dates, values, label = 'Annual Training Stress' }: AnnualHeatmapProps) {
  const heatmap = useMemo(() => buildAnnualHeatmap(dates, values), [dates, values]);
  const max = useMemo(() => Math.max(...heatmap.map((d) => d.value), 0), [heatmap]);
  const totalWeeks = heatmap.length > 0 ? heatmap[heatmap.length - 1].week + 1 : 0;
  if (!heatmap.length) return null;

  return (
    <div className="rounded-xl border border-border/60 p-4">
      <h3 className="text-sm font-semibold mb-3">{label}</h3>
      <TooltipProvider delayDuration={100}>
        <div className="overflow-x-auto">
          <div className="inline-flex gap-0.5">
            <div className="flex flex-col gap-0.5 mr-1">
              {DAY_LABELS.map((lbl, i) => (
                <div key={i} className="h-[11px] flex items-center justify-end">
                  <span className="text-[8px] text-muted-foreground">{lbl}</span>
                </div>
              ))}
            </div>
            {Array.from({ length: totalWeeks }, (_, weekIdx) => {
              const weekDays = heatmap.filter((d) => d.week === weekIdx);
              return (
                <div key={weekIdx} className="flex flex-col gap-0.5">
                  {Array.from({ length: 7 }, (_, dayIdx) => {
                    const day = weekDays.find((d) => d.weekday === dayIdx);
                    if (!day) return <div key={dayIdx} className="h-[11px] w-[11px]" />;
                    const l = level(day.value, max);
                    return (
                      <Tooltip key={dayIdx}>
                        <TooltipTrigger asChild>
                          <div className={`h-[11px] w-[11px] rounded-[2px] ${COLORS[l]} transition-colors`} />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="text-xs">
                          <span className="font-medium">{day.date}</span>
                          <span className="text-muted-foreground ml-2">{day.value.toFixed(0)} TSS</span>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </TooltipProvider>
      <div className="flex items-center gap-2 mt-3">
        <span className="text-[10px] text-muted-foreground">Less</span>
        {COLORS.map((c, i) => <div key={i} className={`h-[11px] w-[11px] rounded-[2px] ${c}`} />)}
        <span className="text-[10px] text-muted-foreground">More</span>
      </div>
    </div>
  );
}
